import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { InspectionBadge } from '@/components/StatusBadge';
import { shortDate } from '@/lib/format';
import { useCreateInspection, useInspections } from '@/lib/queries';
import { useSite } from '@/lib/useSite';
import type { InspectionType, Trade } from '@/lib/types';

const TRADES: Trade[] = ['건축', '구조', '기계', '전기', '통신', '토목', '조경', '기타'];
const TYPES: InspectionType[] = ['자재', '시공', '기타'];

/**
 * 검측 목록.
 *
 * <p>요청은 협력사가, 판정은 감리가 한다 — 감리는 둘 다 하지 못한다(DOC-82 §4-1). 직원은 둘 다 한다:
 * 시공사 자체 검측은 직원이 요청하고 직원이 판정한다(DOC-87 §2-3). 「요청」 버튼은 증빙을 만드는
 * 손({@code canSubmitEvidence})에게 선다.
 */
export function InspectionsPage() {
  const { siteId, siteName, canSubmitEvidence } = useSite();
  const inspections = useInspections(siteId);
  const create = useCreateInspection(siteId);

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [inspectionType, setInspectionType] = useState<InspectionType>('시공');
  const [trade, setTrade] = useState<Trade>('건축');
  const [location, setLocation] = useState('');

  const rows = inspections.data ?? [];
  const canRequest = canSubmitEvidence;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    await create.mutateAsync({
      title: title.trim(),
      inspectionType,
      trade,
      location: location.trim() === '' ? null : location.trim(),
      dueDate: null,
    });
    setOpen(false);
    setTitle('');
    setLocation('');
  }

  return (
    <>
      <PageHeader
        title="검측"
        subtitle={siteName}
        action={
          canRequest && (
            <button type="button" className="btn-primary px-3 py-1.5 text-ui-xs" onClick={() => setOpen(true)}>
              요청
            </button>
          )
        }
      />

      {open && (
        <form onSubmit={onSubmit} className="card mb-3">
          {/* 패드 가로에서는 두 열 — 제목·위치가 한 줄, 구분·공종이 한 줄. 세로·폰에서는 한 열로 흐른다. */}
          <div className="mb-4 grid gap-3 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>제목</span>
              <input type="text" className="input-field" value={title} onChange={(e) => setTitle(e.target.value)} required />
            </label>
            <label className="block">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>위치</span>
              <input type="text" className="input-field" value={location} onChange={(e) => setLocation(e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>구분</span>
              <select className="input-field" value={inspectionType} onChange={(e) => setInspectionType(e.target.value as InspectionType)}>
                {TYPES.map((it) => <option key={it} value={it}>{it}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>공종</span>
              <select className="input-field" value={trade} onChange={(e) => setTrade(e.target.value as Trade)}>
                {TRADES.map((it) => <option key={it} value={it}>{it}</option>)}
              </select>
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1" disabled={create.isPending}>
              {create.isPending ? '보내는 중입니다…' : '요청'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>취소</button>
          </div>
        </form>
      )}

      <QueryState
        pending={inspections.isPending}
        error={inspections.isError}
        empty={rows.length === 0}
        emptyText="검측 요청이 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id}>
              <Link to={`/sites/${siteId}/inspections/${row.id}`} className="card block">
                <div className="mb-1 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-ui-md font-medium">
                      {row.title}
                      {row.roundNo > 1 && (
                        <span className="ml-1 text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                          {row.roundNo}차
                        </span>
                      )}
                    </div>
                    <div className="truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                      {row.inspectionType} · {row.trade ?? '공종 미지정'}
                      {row.location !== null && ` · ${row.location}`}
                    </div>
                  </div>
                  <InspectionBadge status={row.status} />
                </div>
                <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                  {shortDate(row.inspectionDate)}
                  {row.photoCount > 0 && ` · 사진 ${row.photoCount}장`}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </QueryState>
    </>
  );
}
